import {VictoryPointTypes} from '@colonist/game-base-types'
import {CardDataController} from '@colonist/game-card-types'
import {TranslationObject} from '@colonist/i18n'
import {clientI18n} from '../../../ClientI18n'
import {UIUtilsEjsVariables} from '../../../UIUtilsEjsVariables'
import {createDivIn, createExternalImgIn, createParagraphIn, setElementInnerHTML} from '../../../ui_utils/DomUtils'
import {UIGameManager} from '../../UIGameManager'
import {
    CostInformation,
    getCostInformation, getPieceIconPath,
} from './UIGameCostsInformation'
import {VPInformation, getVPInformation, getVPTypeIconPath} from './UIGameVPInformation'
import {PlayerColor} from '@colonist/game-states'
import {getImagePath} from '../../../ui_utils/Utils'
import {createTooltipIn} from '../../../ui_utils/TooltipUtils.ts'

export class UIGameInformationView {
    private readonly container: HTMLDivElement
    private readonly uiGameManager: UIGameManager

    private informationContent: HTMLDivElement
    private readonly costsInformationContainer: HTMLDivElement
    private readonly vpInformationContainer: HTMLDivElement
    private title: HTMLParagraphElement
    private arrowsContainer: HTMLDivElement
    private readonly view: UIGameInformationView

    constructor(container: HTMLDivElement, uiGameManager: UIGameManager) {
        this.container = container
        this.uiGameManager = uiGameManager

        this.createTitleSection()

        this.costsInformationContainer = createDivIn(this.informationContent, 'information-container')
        this.generateCostsInformationContent()

        this.vpInformationContainer = createDivIn(this.informationContent, 'information-container')
        this.generateVPInformationContent()
        this.costsInformationContainer.style.display = 'none'
    }

    overrideContentForBeginnerMode(actionIndex: number) {
        this.hideArrows()

        this.setTitleTextForBeginnerMode(actionIndex)
        this.setBodyTextForBeginnerMode(actionIndex)
    }

    private hideArrows() {
        if(this.arrowsContainer.style.display == 'none') return
        this.arrowsContainer.style.display = 'none'
    }

    private setTitleTextForBeginnerMode(actionIndex: number) {
        const titleText = this.getExplanationText(actionIndex, true)
        setElementInnerHTML(this.title, titleText)
    }

    private setBodyTextForBeginnerMode(actionIndex: number) {
        const bodyText = this.getExplanationText(actionIndex)
        const emptyString: TranslationObject = {key: 'strings:colonistCommon.utils.emptyString'}
        setElementInnerHTML(this.vpInformationContainer, emptyString)
        const bodyContainer = createDivIn(this.vpInformationContainer, 'in-game-information-row')
        createParagraphIn(bodyContainer, bodyText, '')
    }

    private generateCostsInformationContent() {
        const gameMode = this.uiGameManager.gameState.gameModeData.modeSetting
        const costsInformation: CostInformation[] = getCostInformation(gameMode)

        const costsSubtitleRow = createDivIn(this.costsInformationContainer, 'in-game-information-row')
        const costsSubtitle = createDivIn(costsSubtitleRow, 'in-game-info-description')
        createParagraphIn(costsSubtitle, {key: 'strings:game.buildCosts.description'}, '')

        for(const costInformation of costsInformation) {
            const costInformationRow = createDivIn(this.costsInformationContainer, 'in-game-information-row')
            const costDescription = createDivIn(costInformationRow, 'in-game-info-description')
            const pieceTextureColored = getPieceIconPath(costInformation.piece, this.uiGameManager.gameController.myColor)

            createExternalImgIn(costDescription, pieceTextureColored, {key: 'strings:colonistCommon.utils.emptyString'}, 'piece-icon')
            createParagraphIn(costDescription, costInformation.title, '')

            for(const requiredCard of costInformation.cost) {
                const cardData = CardDataController.getCardDataForCard(requiredCard)
                if(cardData == undefined) continue
                createExternalImgIn(costInformationRow, getImagePath(`${cardData.imageFileName}.svg`), {key: 'strings:colonistCommon.utils.emptyString'})
            }
        }
    }

    private createTitleSection() {
        const informationContentClass = UIUtilsEjsVariables.isUserOnDiscord ? 'in-game-information-content no-fullscreen-button' : 'in-game-information-content'
        this.informationContent = createDivIn(this.container, informationContentClass)

        const titleKey = 'strings:game.endGame.icons.trophy'
        const titleContainer = createDivIn(this.informationContent, 'in-game-information-title-container')
        this.title = createParagraphIn(titleContainer, {key: titleKey}, 'in-game-information-title')

        this.createArrows(titleContainer)
    }

    private createArrows(titleContainer: HTMLDivElement) {
        this.arrowsContainer = createDivIn(titleContainer, 'in-game-information-title-container arrows-container')
        const leftArrowContainer = createDivIn(this.arrowsContainer, '')
        const rightArrowContainer = createDivIn(this.arrowsContainer, '')

        createExternalImgIn(leftArrowContainer, getImagePath('icon_down_arrow.png'), {key: 'strings:colonistCommon.utils.emptyString'}, 'arrows left')

        createExternalImgIn(rightArrowContainer, getImagePath('icon_down_arrow.png'), {key: 'strings:colonistCommon.utils.emptyString'}, 'arrows right')

        this.arrowsContainer.addEventListener('click', () => this.switchContext())
    }

    private switchContext() {
        const isVPInformationVisible = this.vpInformationContainer.style.display != 'none'
        if(isVPInformationVisible) {
            this.title.innerText = clientI18n.t('strings:game.buildCosts.title')
            this.showCostsInformation()
            return
        }
        this.title.innerText = clientI18n.t('strings:game.endGame.icons.trophy')
        this.showVPInformation()
    }

    private generateVPInformationContent() {
        const gameMode = this.uiGameManager.gameState.gameModeData.modeSetting
        const vpInformations: VPInformation[] = getVPInformation(gameMode)

        this.createVPInformationSubtitle()

        for(const vpInformation of vpInformations) {
            const vpInformationRow = createDivIn(this.vpInformationContainer, 'in-game-information-row')

            const vpDescription = createDivIn(vpInformationRow, 'in-game-info-description')

            const iconContainer = createDivIn(vpDescription, 'piece-icon-container')
            const pieceTextureColored = getVPTypeIconPath(vpInformation, this.uiGameManager.gameController.myColor)
            createExternalImgIn(iconContainer, pieceTextureColored, {key: 'strings:colonistCommon.utils.emptyString'}, 'piece-icon')
            createParagraphIn(vpDescription, vpInformation.title, '')

            if(vpInformation.description != undefined) {
                createTooltipIn(vpDescription, vpInformation.description)
            }

            if(vpInformation.secondaryIcon != undefined) {
                const secondaryIconClass = vpInformation.type == VictoryPointTypes.Metropolis ? 'piece-icon-secondary-metropolis' : 'piece-icon-secondary'
                createExternalImgIn(iconContainer, getImagePath(vpInformation.secondaryIcon), {key: 'strings:colonistCommon.utils.emptyString'}, secondaryIconClass)
            }

            const vpAmount = createDivIn(vpInformationRow, 'vp-amount')
            const vpAmountTranslation: TranslationObject = {key: 'strings:colonistCommon.utils.#buffer', options: {value: `+${vpInformation.vpAmount}`}}
            createParagraphIn(vpAmount, vpAmountTranslation, '')
            createExternalImgIn(vpAmount, getImagePath('icon_trophy.svg'), {key: 'strings:colonistCommon.utils.emptyString'}, 'trophy-icon')
        }
    }

    private showVPInformation() {
        this.costsInformationContainer.style.display = 'none'
        this.vpInformationContainer.style.display = 'contents'
    }

    private showCostsInformation() {
        this.costsInformationContainer.style.display = 'contents'
        this.vpInformationContainer.style.display = 'none'
    }

    private createVPInformationSubtitle() {
        const vpToWin = this.uiGameManager.gameController.gameSettings.victoryPointsToWin
        const vpSubtitleRow = createDivIn(this.vpInformationContainer, 'in-game-information-row')
        const vpSubtitle = createDivIn(vpSubtitleRow, 'in-game-info-description')
        createParagraphIn(vpSubtitle, {key: 'strings:game.beginnerTips.reach'}, '')
        createParagraphIn(vpSubtitle, {key: 'strings:colonistCommon.utils.#buffer', options: {value: `${vpToWin}`}}, 'vp-to-win-amount')
        createExternalImgIn(vpSubtitle, getImagePath('icon_trophy.svg'), {key: 'strings:colonistCommon.utils.emptyString'}, 'trophy-icon')
        createParagraphIn(vpSubtitle, {key: 'strings:game.beginnerTips.toWinTheGame'}, '')
    }

    // eslint-disable-next-line class-methods-use-this
    private getExplanationText(actionIndex: number, isTitle: boolean = false): TranslationObject {
        const playerColor = PlayerColor[this.uiGameManager.gameController.myColor].toLowerCase()
        const extension = isTitle ? 'title' : 'body'
        switch(actionIndex) {
            case 0: return {key: `strings:game.beginnerMode.explanations.explanation0.${extension}`}
            case 1: return {key: `strings:game.beginnerMode.explanations.explanation1.${extension}`, options: {playerColor: playerColor}}
            case 2: return {key: `strings:game.beginnerMode.explanations.explanation2.${extension}`}
            case 3: return {key: `strings:game.beginnerMode.explanations.explanation3.${extension}`}
            case 4: return {key: `strings:game.beginnerMode.explanations.explanation4.${extension}`, options: {playerColor: playerColor}}
            case 5: return {key: `strings:game.beginnerMode.explanations.explanation5.${extension}`}
            case 6: return {key: `strings:game.beginnerMode.explanations.explanation6.${extension}`}
            case 7: return {key: `strings:game.beginnerMode.explanations.explanation7.${extension}`}
            case 8: return {key: `strings:game.beginnerMode.explanations.explanation8.${extension}`}
            case 9: return {key: `strings:game.beginnerMode.explanations.explanation9.${extension}`}
            case 10: return {key: `strings:game.beginnerMode.explanations.explanation10.${extension}`}
            case 11: return {key: `strings:game.beginnerMode.explanations.explanation11.${extension}`, options: {playerColor: playerColor}}
            case 12: return {key: `strings:game.beginnerMode.explanations.explanation12.${extension}`, options: {playerColor: playerColor}}
            case 13: return {key: `strings:game.beginnerMode.explanations.explanation13.${extension}`, options: {playerColor: playerColor}}
            case 14:
            case 15:
            case 16:
            case 17: return {key: `strings:game.beginnerMode.explanations.explanation14.${extension}`, options: {playerColor: playerColor}}
            case 18: return {key: `strings:game.beginnerMode.explanations.explanation18.${extension}`, options: {playerColor: playerColor}}
            case 19:
            case 20:
            case 21: return {key: `strings:game.beginnerMode.explanations.explanation19.${extension}`}
            case 22: return {key: `strings:game.beginnerMode.explanations.explanation22.${extension}`, options: {playerColor: playerColor}}
            default: return {key: 'strings:colonistCommon.utils.bug'}
        }
    }
}
